/**
 * Sprint 76 — La espera como estado del pedido a domicilio.
 *
 * El flujo real del negocio tiene tres esperas que **no dependen de él**: que el cliente mande
 * su ubicación, que el grupo de motomandados conteste la cotización y que llegue la captura de
 * la transferencia. El asistente de cinco pasos las modelaba como si no existieran —se abre, se
 * llena y no se puede soltar—, así que con dos clientes escribiendo a la vez había que terminar
 * uno para empezar el otro.
 *
 * Aquí se **derivan** los motivos de espera de los datos que el pedido ya tiene. Ni un estado
 * nuevo en la máquina de estados: meter uno entre `abierto` y `pendiente_cocina` haría
 * desaparecer pedidos de toda pantalla que filtre por estados literales, que es exactamente por
 * lo que `fechaEmpaquetado` (Sprint 66) es una marca de tiempo y no un estado.
 *
 * Módulo puro: lo usan el servicio (que arma el tablero) y la pantalla (que lo pinta), y se
 * prueba entero sin base de datos.
 */

/** En qué está atorado —o en qué va— un pedido a domicilio vivo. */
export type MotivoEspera =
  /**
   * Sprint 92 — Llegó por el enlace del menú y **nadie lo ha visto todavía**.
   *
   * Va antes que cualquier otro motivo porque es el único que no lo tomó una persona: no se sabe
   * si el cliente existe, si el pedido tiene sentido, ni si es una broma. Todo lo demás del
   * tablero es trabajo del negocio; esto es trabajo **entrante**.
   */
  | 'llego_del_enlace'
  /** Falta preguntarle al grupo cuánto cobran por llevarlo. */
  | 'falta_cotizar_envio'
  /** Ya se cotizó y se le pasó el total; falta que mande la captura de la transferencia. */
  | 'falta_comprobante'
  /**
   * Sprint 137 — Se acordó cobrarlo al entregar y todavía no salió a la parrilla.
   *
   * Existe por una razón concreta: sin él, un contra entrega caía en `falta_comprobante` —es
   * un domicilio abierto sin pago— y a los quince minutos el tablero preguntaba si cancelarlo,
   * sobre un cliente que **nunca** va a mandar una captura. Esa alerta saldría siempre, y una
   * alerta que siempre es falsa enseña a ignorar las que no lo son.
   */
  | 'contra_entrega_falta_cocina'
  /** Pagado y en manos de la cocina. */
  | 'en_cocina'
  /** Empaquetado o listo, esperando que llegue la moto. */
  | 'listo_esperando_moto'
  /** Salió con el repartidor. */
  | 'en_ruta'
  /** Se entregó y aún no se cobró (envío contra entrega / efectivo). */
  | 'entregado_falta_cobro';

/** Lo mínimo que hay que saber de un pedido para ubicarlo en el tablero. */
export interface PedidoParaEspera {
  tipo: string;
  estado: string;
  estadoPago: string;
  /** Sprint 92 — 'mostrador' | 'enlace'. Ausente = capturado por el negocio, como siempre. */
  origen?: string | null;
  /** ISO o Date. `null` = todavía no se cotizó el envío. */
  fechaCotizacionEnvio?: string | Date | null;
  fechaCreacion: string | Date;
  /** Prórroga acordada para el comprobante. */
  esperaPagoHasta?: string | Date | null;
  /** Sprint 137 — Se acordó cobrarlo al entregar: no hay comprobante que esperar. */
  pagaAlEntregar?: boolean | null;
  fechaSalida?: string | Date | null;
  fechaEmpaquetado?: string | Date | null;
}

/** Minutos que el negocio le da a un cliente para mandar su comprobante. */
export const MINUTOS_ESPERA_PAGO_DEFAULT = 15;

/** Clave de configuración donde el dueño ajusta ese tiempo. */
export const CLAVE_MINUTOS_ESPERA_PAGO = 'domicilio_minutos_espera_pago';

/** Clave donde el dueño guarda banco/titular/CLABE que se le dictan al cliente. */
export const CLAVE_DATOS_PAGO = 'domicilio_datos_pago';

/** Los estados que ya no están vivos: nada que esperar. */
const TERMINALES = new Set(['pagado', 'cancelado', 'no_entregado']);

function aFecha(v: string | Date | null | undefined): Date | null {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * En qué está este pedido, o `null` si no pinta en el tablero (no es a domicilio, o ya cerró).
 *
 * El orden de las preguntas importa: primero lo que **bloquea la cocina**, porque es lo que
 * cuesta dinero y clientes.
 */
export function motivoDeEspera(p: PedidoParaEspera): MotivoEspera | null {
  if (p.tipo !== 'delivery') return null;
  if (TERMINALES.has(p.estado)) return null;

  if (p.estado === 'abierto') {
    /**
     * Sprint 92 — Lo que entró solo, primero. Un pedido del enlace no descontó inventario ni tocó
     * la cocina: lo único que le falta es que alguien lo mire. Confundirlo con "falta cotizar"
     * escondería justo el paso que lo vuelve real.
     */
    if (p.origen === 'enlace' && !aFecha(p.fechaCotizacionEnvio)) return 'llego_del_enlace';
    /**
     * Sin la marca de cotización, `costoEnvio = 0` significaría a la vez "todavía no pregunto"
     * y "se lo llevo gratis". Por eso la marca es lo que se consulta, y no el monto.
     */
    if (!aFecha(p.fechaCotizacionEnvio)) return 'falta_cotizar_envio';
    /**
     * Sprint 137 — Un contra entrega ya cotizado no espera dinero: espera la parrilla.
     *
     * Va **antes** de la pregunta por el pago, porque la respuesta a esa pregunta siempre va a
     * ser "no está pagado" —ése es justamente el trato— y contestarla aquí lo mandaría a la
     * columna de esperas con un reloj corriendo hacia una alerta que no significa nada.
     */
    if (p.pagaAlEntregar === true) return 'contra_entrega_falta_cocina';
    if (p.estadoPago !== 'pagado') return 'falta_comprobante';
    /**
     * Pagado pero todavía `abierto`: el comprobante entró y el envío a cocina no llegó a
     * ejecutarse (se cerró la app, falló la impresión). No es un caso teórico y no debe quedar
     * invisible: se muestra como si faltara la comanda, que es lo que hay que resolver.
     */
    return 'falta_comprobante';
  }

  if (p.estado === 'pendiente_cocina' || p.estado === 'cocina') return 'en_cocina';
  if (p.estado === 'listo') return 'listo_esperando_moto';
  if (p.estado === 'en_ruta') return 'en_ruta';
  if (p.estado === 'entregado') return 'entregado_falta_cobro';
  return null;
}

/** Desde cuándo se cuenta el reloj de cada motivo. */
function desdeCuando(p: PedidoParaEspera, motivo: MotivoEspera): Date | null {
  switch (motivo) {
    case 'en_ruta':
      return aFecha(p.fechaSalida) ?? aFecha(p.fechaCreacion);
    case 'listo_esperando_moto':
      return aFecha(p.fechaEmpaquetado) ?? aFecha(p.fechaCreacion);
    case 'falta_comprobante':
      return aFecha(p.fechaCotizacionEnvio) ?? aFecha(p.fechaCreacion);
    default:
      return aFecha(p.fechaCreacion);
  }
}

/**
 * Cuántos minutos lleva esperando **eso** que espera.
 *
 * No se cuenta desde que se creó el pedido sino desde que empezó la espera actual: un pedido que
 * lleva una hora pero que salió hace dos minutos no tiene ningún problema, y pintarlo en rojo
 * sería enseñarle al operador a ignorar los rojos.
 */
export function minutosEsperando(p: PedidoParaEspera, ahora: Date): number {
  const motivo = motivoDeEspera(p);
  if (!motivo) return 0;
  const desde = desdeCuando(p, motivo);
  if (!desde) return 0;
  const min = Math.floor((ahora.getTime() - desde.getTime()) / 60000);
  return min > 0 ? min : 0;
}

/**
 * ¿Se le acabó el tiempo para mandar el comprobante?
 *
 * Sólo aplica al motivo `falta_comprobante`: es el único con un plazo que el negocio fijó (15
 * minutos, configurables). Si alguien concedió una prórroga, manda la prórroga.
 *
 * **Esto no cancela nada.** Devuelve `true` para que la pantalla pregunte; la decisión de
 * esperar más, recordarle o cancelar es de la persona, porque el propio negocio dijo que a un
 * cliente conocido se le considera. Un sistema que cancelara por reloj le quitaría esa decisión
 * y el día que tirara el pedido de un cliente bueno, nadie volvería a confiar en el tablero.
 */
export function esperaDePagoVencida(
  p: PedidoParaEspera,
  ahora: Date,
  minutosLimite: number = MINUTOS_ESPERA_PAGO_DEFAULT,
): boolean {
  if (motivoDeEspera(p) !== 'falta_comprobante') return false;

  const prorroga = aFecha(p.esperaPagoHasta);
  if (prorroga) return ahora.getTime() >= prorroga.getTime();

  const limite = Number.isFinite(minutosLimite) && minutosLimite > 0
    ? minutosLimite
    : MINUTOS_ESPERA_PAGO_DEFAULT;
  return minutosEsperando(p, ahora) >= limite;
}

/** Cómo se lee cada motivo en el tablero. En palabras del negocio, no del código. */
export const ETIQUETA_MOTIVO: Record<MotivoEspera, string> = {
  llego_del_enlace: 'Nuevo · lo armó el cliente',
  falta_cotizar_envio: 'Falta el costo del envío',
  falta_comprobante: 'Falta el comprobante',
  contra_entrega_falta_cocina: 'Se cobra al entregar · falta mandarlo',
  en_cocina: 'En cocina',
  listo_esperando_moto: 'Listo · esperando moto',
  en_ruta: 'En ruta',
  entregado_falta_cobro: 'Entregado · falta cobrar',
};

/** Las columnas del tablero, en el orden en que ocurren. */
export const COLUMNAS_TABLERO: readonly {
  clave: 'entrantes' | 'esperando' | 'cocina' | 'listo' | 'ruta';
  titulo: string;
  motivos: readonly MotivoEspera[];
}[] = [
  /**
   * Sprint 92/93 — Primera columna: **lo que llegó solo y nadie ha visto**.
   *
   * Va antes que todo porque es lo único que puede ser mentira. El resto del tablero son pedidos
   * que una persona del negocio ya tomó; esto es trabajo entrante que hay que revisar antes de
   * gastarle comida.
   */
  {
    clave: 'entrantes',
    titulo: 'Llegaron del enlace',
    motivos: ['llego_del_enlace'],
  },
  {
    clave: 'esperando',
    titulo: 'Esperando respuesta',
    /**
     * Sprint 137 — El contra entrega vive aquí, pero sin reloj.
     *
     * Se queda en esta columna y no gana una propia: son cinco columnas que ya representan
     * cinco cosas que hacer, y una sexta en hora pico es una más que mirar. Lo que lo
     * distingue es el motivo y el color de la tarjeta, no su lugar.
     */
    motivos: ['falta_cotizar_envio', 'falta_comprobante', 'contra_entrega_falta_cocina'],
  },
  { clave: 'cocina', titulo: 'En cocina', motivos: ['en_cocina'] },
  { clave: 'listo', titulo: 'Listo · esperando moto', motivos: ['listo_esperando_moto'] },
  { clave: 'ruta', titulo: 'En ruta', motivos: ['en_ruta', 'entregado_falta_cobro'] },
];

/**
 * Lee el ajuste de minutos guardado como texto.
 *
 * Tolerante a lo que haya en la base: un valor vacío, no numérico o absurdo cae al default en
 * lugar de dejar el reloj en cero (que marcaría **todos** los pedidos como vencidos en cuanto
 * se capturan) o en infinito (que no marcaría ninguno).
 */
export function leerMinutosEspera(valor: string | null | undefined): number {
  const n = Number((valor ?? '').trim());
  if (!Number.isFinite(n)) return MINUTOS_ESPERA_PAGO_DEFAULT;
  const entero = Math.round(n);
  if (entero < 1 || entero > 24 * 60) return MINUTOS_ESPERA_PAGO_DEFAULT;
  return entero;
}
